import { valueAt } from "../common/valueAt";
import type { AlgorithmOperation, OperationType } from "@algoquest/schemas";

/**
 * Binary Search — framework-independent Algorithm Engine (Master Spec §8,
 * §13, §23). Requires a sorted (ascending) dataset. Unlike Linear Search,
 * the player has a real decision each round: after inspecting the middle
 * element, they must choose which half to discard. The engine — not the
 * UI — is the single source of truth for whether that choice is correct;
 * a wrong choice is rejected (counted as a mistake) rather than silently
 * auto-corrected, so the player learns by getting immediate feedback.
 *
 * Two-phase interaction per round: inspect() reveals+compares the current
 * mid; chooseDirection() commits to discarding a half. The correct
 * direction is never exposed on the public domain state — only whether a
 * submitted choice was right — so the UI cannot hand players the answer.
 */
export interface BinarySearchDomainState {
  values: number[];
  target: number;
  low: number;
  high: number;
  mid: number | null;
  awaitingChoice: boolean;
  eliminatedRanges: Array<[number, number]>;
  checkedIndices: number[];
  foundIndex: number | null;
  comparisons: number;
  mistakes: number;
  isComplete: boolean;
}

export interface BinarySearchStepResult {
  operations: AlgorithmOperation[];
  state: BinarySearchDomainState;
}

export interface BinarySearchChoiceResult {
  wasCorrect: boolean;
  result: BinarySearchStepResult | null;
}

export class BinarySearchEngine {
  private readonly values: number[];
  private readonly target: number;
  private low: number;
  private high: number;
  private mid: number | null;
  private awaitingChoice = false;
  private eliminatedRanges: Array<[number, number]> = [];
  private checkedIndices: number[] = [];
  private foundIndex: number | null = null;
  private comparisons = 0;
  private mistakes = 0;
  private isComplete = false;
  private sequenceCounter = 0;
  private readonly trace: AlgorithmOperation[] = [];

  constructor(values: number[], target: number) {
    if (values.length === 0) {
      throw new Error("BinarySearchEngine requires a non-empty dataset");
    }
    this.values = values;
    this.target = target;
    this.low = 0;
    this.high = values.length - 1;
    this.mid = Math.floor((this.low + this.high) / 2);
  }

  getState(): BinarySearchDomainState {
    return {
      values: this.values,
      target: this.target,
      low: this.low,
      high: this.high,
      mid: this.mid,
      awaitingChoice: this.awaitingChoice,
      eliminatedRanges: this.eliminatedRanges.map(([start, end]) => [start, end] as [number, number]),
      checkedIndices: [...this.checkedIndices],
      foundIndex: this.foundIndex,
      comparisons: this.comparisons,
      mistakes: this.mistakes,
      isComplete: this.isComplete,
    };
  }

  getTrace(): AlgorithmOperation[] {
    return [...this.trace];
  }

  /**
   * Reveal and compare the current mid element. Only valid when a round is
   * not already awaiting a direction choice and the search isn't complete.
   */
  inspect(nowMs: number): BinarySearchStepResult | null {
    if (this.isComplete || this.awaitingChoice || this.mid === null) {
      return null;
    }

    const index = this.mid;
    const value = valueAt(this.values, index);
    this.comparisons += 1;
    this.checkedIndices.push(index);

    const operations: AlgorithmOperation[] = [
      this.emit("SCAN", nowMs, { indices: [index], values: [value] }),
    ];

    if (value === this.target) {
      this.foundIndex = index;
      this.isComplete = true;
      this.mid = null;
      operations.push(this.emit("FOUND", nowMs, { indices: [index], values: [value] }));
      return { operations, state: this.getState() };
    }

    operations.push(this.emit("COMPARE", nowMs, { indices: [index], values: [value] }));
    this.awaitingChoice = true;
    return { operations, state: this.getState() };
  }

  /**
   * Commit to discarding the left or right half of the current range. If
   * `direction` doesn't match the true direction implied by the last
   * inspection, nothing changes except the mistake counter — the caller
   * must let the player try again rather than silently correcting them.
   */
  chooseDirection(direction: "left" | "right", nowMs: number): BinarySearchChoiceResult {
    if (this.isComplete || !this.awaitingChoice || this.mid === null) {
      return { wasCorrect: false, result: null };
    }

    const midIndex = this.mid;
    const midValue = valueAt(this.values, midIndex);
    const correctDirection: "left" | "right" = this.target < midValue ? "left" : "right";

    if (direction !== correctDirection) {
      this.mistakes += 1;
      return { wasCorrect: false, result: null };
    }

    let eliminatedRange: [number, number];
    if (direction === "left") {
      eliminatedRange = [midIndex, this.high];
      this.high = midIndex - 1;
    } else {
      eliminatedRange = [this.low, midIndex];
      this.low = midIndex + 1;
    }
    this.eliminatedRanges.push(eliminatedRange);
    this.awaitingChoice = false;

    const operations: AlgorithmOperation[] = [
      this.emit(direction === "left" ? "CHOOSE_LEFT" : "CHOOSE_RIGHT", nowMs, {}),
      this.emit("DISCARD_RANGE", nowMs, { range: eliminatedRange }),
    ];

    if (this.low > this.high) {
      this.isComplete = true;
      this.mid = null;
      operations.push(this.emit("NOT_FOUND", nowMs, {}));
    } else {
      this.mid = Math.floor((this.low + this.high) / 2);
    }

    return { wasCorrect: true, result: { operations, state: this.getState() } };
  }

  private emit(
    type: OperationType,
    timestampMs: number,
    partial: Partial<Omit<AlgorithmOperation, "sequence" | "type" | "timestampMs">>,
  ): AlgorithmOperation {
    const operation: AlgorithmOperation = {
      sequence: this.sequenceCounter,
      type,
      timestampMs,
      ...partial,
    };
    this.sequenceCounter += 1;
    this.trace.push(operation);
    return operation;
  }
}
